import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function CancelledSubscriptions() {
  const [q, setQ] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    staffApi.get('/subscriptions', { params: { status: 'CANCELLED', q, pageSize: 100 } }).then(({ data }) => {
      setSubscriptions(data.subscriptions);
      setTotal(data.total);
    });

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const reactivate = async (id) => {
    setBusyId(id);
    try {
      await staffApi.patch(`/subscriptions/${id}/reactivate`);
      toast.success('Subscription reactivated');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <Link to="/admin/subscriptions" className="inline-flex items-center gap-1 text-sm text-muted-ink hover:text-saffron mb-1" data-testid="back-to-subscriptions">
            <ArrowLeft size={14} /> Subscriptions
          </Link>
          <h1 className="font-heading text-2xl font-700 text-charcoal">Cancelled Tiffins</h1>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-ink" />
        <Input
          data-testid="cancelled-search-input"
          placeholder="Search by customer name, phone, email or ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Cancelled Date</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.map((s) => (
              <TableRow key={s.id} data-testid="cancelled-subscription-row" className="hover:bg-bone/50 transition-colors duration-150">
                <TableCell><Link to={`/admin/subscriptions/${s.id}`} className="font-medium text-charcoal block">{s.customer.name}</Link></TableCell>
                <TableCell>{s.customer.phone}</TableCell>
                <TableCell>{s.plan.name}</TableCell>
                <TableCell>{s.remainingCredits}/{s.originalCredits}</TableCell>
                <TableCell data-testid="cancelled-date">{s.cancelledAt ? new Date(s.cancelledAt).toLocaleDateString('en-CA') : '—'}</TableCell>
                <TableCell className="max-w-xs" data-testid="cancelled-reason">{s.cancellationReason || '—'}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`reactivate-btn-${s.id}`}
                    variant="outline"
                    size="sm"
                    disabled={busyId === s.id}
                    className="border-coriander text-coriander hover:bg-coriander/10"
                    onClick={() => reactivate(s.id)}
                  >
                    {busyId === s.id ? 'Working…' : 'Reactivate'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {subscriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-ink py-8">No cancelled subscriptions.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-ink">{total} cancelled subscription(s)</p>
    </div>
  );
}
